import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, AlertTriangle, RefreshCw, ChevronRight } from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KpiStrip } from '@/components/common/KpiStrip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTriage } from '@/hooks/useTriage';
import { FilterChip } from '@/components/common/FilterChip';
import type { TriagePriority, TriageCategory, TriageItem } from '@/types/metrics';

const PRIORITY_CONFIG: Record<TriagePriority, { label: string; color: string; bg: string }> = {
  P1: { label: 'P1 Critical', color: 'dls-text-status-offline', bg: 'dls-bg-status-offline-quarternary dls-border-status-offline-tertiary' },
  P2: { label: 'P2 High', color: 'dls-text-status-warning', bg: 'dls-bg-status-warning-quarternary dls-border-status-warning-tertiary' },
  P3: { label: 'P3 Medium', color: 'dls-text-status-warning', bg: 'dls-bg-status-warning-quarternary dls-border-status-warning-tertiary' },
  P4: { label: 'P4 Low', color: 'dls-text-status-info', bg: 'dls-bg-status-info/15 dls-border-status-info/30' },
};

const CATEGORY_LABELS: Record<TriageCategory, string> = {
  node_offline: 'Node Offline',
  service_crash: 'Service Crash',
  resource_critical: 'Resource Critical',
  resource_caution: 'Resource Caution',
  cert_expiry: 'Cert Expiry',
  tunnel_down: 'Tunnel Down',
  node_flapping: 'Node Flapping',
  version_outdated: 'Version Outdated',
  user_audit: 'User Audit',
  cluster_degraded: 'Cluster Degraded',
  ntp_desync: 'NTP Desync',
  stale_node: 'Stale Node',
};

function PriorityBadge({ priority }: { priority: TriagePriority }) {
  const config = PRIORITY_CONFIG[priority];
  return (
    <span
      className={`dls-inline-flex dls-items-center dls-px-2 dls-py-0.5 dls-rounded dls-text-xs dls-font-semibold dls-border ${config.bg} ${config.color}`}
    >
      {config.label}
    </span>
  );
}

function CategoryBadge({ category }: { category: TriageCategory }) {
  return (
    <span className="dls-inline-flex dls-items-center dls-px-2 dls-py-0.5 dls-rounded dls-text-xs dls-font-medium dls-bg-muted dls-text-muted-foreground">
      {CATEGORY_LABELS[category] ?? category}
    </span>
  );
}

function TriageItemRow({ item }: { item: TriageItem }) {
  return (
    <div className="dls-flex dls-items-start dls-gap-3 dls-px-4 dls-py-3 dls-border-b last:dls-border-b-0 hover:dls-bg-muted/30 dls-transition-colors">
      <div className="dls-pt-0.5">
        <PriorityBadge priority={item.priority} />
      </div>
      <div className="dls-flex-1 dls-min-w-0">
        <div className="dls-flex dls-items-center dls-gap-2">
          <span className="dls-font-medium dls-text-sm dls-truncate">{item.title}</span>
        </div>
        <p className="dls-text-xs dls-text-muted-foreground dls-mt-0.5">{item.description}</p>
        <div className="dls-flex dls-items-center dls-gap-2 dls-mt-1">
          <CategoryBadge category={item.category} />
          {item.portfolio && (
            <span className="dls-text-xs dls-text-muted-foreground">{item.portfolio}</span>
          )}
          <span className="dls-text-xs dls-text-muted-foreground">
            {formatRelativeTime(item.detectedAt)}
          </span>
        </div>
      </div>
      {item.nodeId && (
        <Link
          to={`/nodes/${item.nodeId}`}
          className="dls-flex-shrink-0 dls-text-muted-foreground hover:dls-text-foreground dls-transition-colors"
          title="View node detail"
        >
          <ChevronRight className="dls-h-4 dls-w-4" />
        </Link>
      )}
    </div>
  );
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export function Triage() {
  const [priorityFilter, setPriorityFilter] = useState<TriagePriority | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [portfolioFilter, setPortfolioFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const filters = useMemo(
    () => ({
      priority: priorityFilter !== 'all' ? priorityFilter : undefined,
      category: categoryFilter !== 'all' ? categoryFilter : undefined,
      portfolio: portfolioFilter !== 'all' ? portfolioFilter : undefined,
      search: search || undefined,
    }),
    [priorityFilter, categoryFilter, portfolioFilter, search]
  );

  const { data, loading, refetch } = useTriage(filters);

  const activeFilterCount = [
    priorityFilter !== 'all',
    categoryFilter !== 'all',
    portfolioFilter !== 'all',
    search.length > 0,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setPriorityFilter('all');
    setCategoryFilter('all');
    setPortfolioFilter('all');
    setSearch('');
  };

  // Extract unique portfolios from items for the filter dropdown
  const portfolios = useMemo(() => {
    if (!data) return [];
    const set = new Set<string>();
    data.items.forEach((item) => {
      if (item.portfolio) set.add(item.portfolio);
    });
    return [...set].sort();
  }, [data]);

  // Extract unique categories that have items
  const activeCategories = useMemo(() => {
    if (!data) return [];
    return data.byCategory.map((c) => c.category);
  }, [data]);

  return (
    <div className="dls-py-6 dls-pb-8">
      <div className="dls-space-y-6">
        {/* Header */}
        <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
          <div>
            <h1 className="dls-page-title dls-text-foreground dls-flex dls-items-center dls-gap-2"><AlertTriangle className="dls-h-7 dls-w-7 dls-text-neeve-primary" />Triage</h1>
            <p className="dls-text-sm dls-text-muted-foreground dls-mt-1">
              Unified view of all issues across your fleet, ranked by priority
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={refetch} disabled={loading}>
            <RefreshCw className={`dls-h-4 dls-w-4 dls-mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Strip */}
        {data && (
          <KpiStrip
            items={(['P1', 'P2', 'P3', 'P4'] as TriagePriority[]).map((p) => {
              const count = data.bySeverity.find((s) => s.priority === p)?.count ?? 0;
              return {
                label: PRIORITY_CONFIG[p].label,
                value: count,
                variant: p === 'P1' ? 'danger' as const : p === 'P2' ? 'warning' as const : 'default' as const,
                onClick: () => setPriorityFilter(priorityFilter === p ? 'all' : p),
                active: priorityFilter === p,
              };
            })}
          />
        )}

        {/* Blast Radius Alert */}
        {data?.blastRadius && (
          <Card className="dls-border-status-warning-tertiary dls-bg-status-warning-quarternary">
            <CardContent className="dls-py-3 dls-px-4">
              <div className="dls-flex dls-items-start dls-gap-2">
                <AlertTriangle className="dls-h-5 dls-w-5 dls-text-status-warning dls-mt-0.5 dls-flex-shrink-0" />
                <div>
                  <p className="dls-text-sm dls-font-semibold dls-text-status-warning">
                    Blast Radius: {data.blastRadius.type.replace(/_/g, ' ')}
                  </p>
                  <p className="dls-text-sm dls-text-status-warning dls-mt-0.5">{data.blastRadius.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters + Problem List */}
        <Card>
          <CardHeader className="dls-pb-3">
            <div className="dls-flex dls-items-center dls-justify-between">
              <CardTitle className="dls-text-lg">
                Issues {data ? `(${data.total})` : ''}
              </CardTitle>
              <div className="dls-flex dls-items-center dls-gap-2">
                <div className="dls-relative">
                  <Search className="dls-absolute dls-left-2.5 dls-top-2.5 dls-h-4 dls-w-4 dls-text-muted-foreground" />
                  <Input
                    placeholder="Search issues..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="dls-pl-9 dls-w-full sm:dls-w-64 dls-h-9 dls-rounded-xl"
                  />
                </div>
                <Select
                  value={categoryFilter}
                  onValueChange={(v) => setCategoryFilter(v)}
                >
                  <SelectTrigger className="dls-w-[160px] dls-h-9">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {activeCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {CATEGORY_LABELS[cat] ?? cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={portfolioFilter}
                  onValueChange={(v) => setPortfolioFilter(v)}
                >
                  <SelectTrigger className="dls-w-[160px] dls-h-9">
                    <SelectValue placeholder="Portfolio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Portfolios</SelectItem>
                    {portfolios.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {activeFilterCount > 0 && (
              <div className="dls-flex dls-items-center dls-gap-2 dls-mt-2">
                {priorityFilter !== 'all' && (
                  <FilterChip label={`Priority: ${priorityFilter}`} onRemove={() => setPriorityFilter('all')} />
                )}
                {categoryFilter !== 'all' && (
                  <FilterChip label={`Category: ${CATEGORY_LABELS[categoryFilter as TriageCategory] ?? categoryFilter}`} onRemove={() => setCategoryFilter('all')} />
                )}
                {portfolioFilter !== 'all' && (
                  <FilterChip label={`Portfolio: ${portfolioFilter}`} onRemove={() => setPortfolioFilter('all')} />
                )}
                {search && (
                  <FilterChip label={`Search: "${search}"`} onRemove={() => setSearch('')} />
                )}
                <button
                  onClick={clearFilters}
                  className="dls-text-xs dls-text-muted-foreground hover:dls-text-foreground"
                >
                  Clear all
                </button>
              </div>
            )}
          </CardHeader>
          <CardContent className="dls-p-0">
            {loading && !data ? (
              <div className="dls-flex dls-items-center dls-justify-center dls-py-12">
                <RefreshCw className="dls-h-6 dls-w-6 dls-animate-spin dls-text-muted-foreground" />
              </div>
            ) : data && data.items.length > 0 ? (
              <div className="dls-divide-y">
                {data.items.map((item) => (
                  <TriageItemRow key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="dls-flex dls-flex-col dls-items-center dls-justify-center dls-py-12 dls-text-muted-foreground">
                <AlertTriangle className="dls-h-8 dls-w-8 dls-mb-2 dls-opacity-40" />
                <p className="dls-text-sm">
                  {activeFilterCount > 0
                    ? 'No issues match your filters'
                    : 'No issues detected – your fleet is healthy'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
